import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';

/**
 * iter132 — Textarea with @-mention typeahead.
 * When the user types `@` followed by at least 1 character, we fetch
 * /api/rd/mention-search?q=term and show a floating list. Arrow keys / Enter
 * select; the chosen label gets inserted in place of the fragment.
 */
export default function MentionTextarea({ value, onChange, placeholder, rows = 2, dataTestId }) {
  const { api } = useAuth();
  const taRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [sel, setSel] = useState(0);
  const [active, setActive] = useState(false);
  const [fragment, setFragment] = useState('');
  const [anchorPos, setAnchorPos] = useState(0);
  const debounce = useRef(null);

  // Find any @token at the caret
  const detectMention = (txt, caret) => {
    const left = txt.slice(0, caret);
    const m = left.match(/@([A-Za-z0-9._-]{0,40})$/);
    if (!m) return null;
    return { frag: m[1], start: caret - m[0].length };
  };

  const handleChange = (e) => {
    const v = e.target.value;
    const caret = e.target.selectionStart || 0;
    onChange(v);
    const m = detectMention(v, caret);
    if (m) {
      setActive(true);
      setAnchorPos(m.start);
      setFragment(m.frag);
      clearTimeout(debounce.current);
      debounce.current = setTimeout(async () => {
        try {
          const r = await api().get(`/rd/mention-search?q=${encodeURIComponent(m.frag)}`);
          setSuggestions(r.data || []);
          setSel(0);
        } catch { setSuggestions([]); }
      }, 150);
    } else {
      setActive(false);
      setSuggestions([]);
    }
  };

  const pick = (opt) => {
    if (!opt) return;
    const ta = taRef.current;
    const before = value.slice(0, anchorPos);
    const after = value.slice((taRef.current?.selectionStart) || value.length);
    // Insert "@<label-without-spaces>" + trailing space
    const insertion = '@' + (opt.label || '').replace(/\s+/g, '') + ' ';
    const next = before + insertion + after;
    onChange(next);
    setActive(false);
    setSuggestions([]);
    setTimeout(() => {
      if (ta) {
        const pos = before.length + insertion.length;
        ta.focus();
        ta.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const onKeyDown = (e) => {
    if (!active || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(s + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(s - 1, 0)); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(suggestions[sel]); }
    else if (e.key === 'Escape') { setActive(false); setSuggestions([]); }
  };

  useEffect(() => () => clearTimeout(debounce.current), []);

  return (
    <div className="relative w-full">
      <textarea
        ref={taRef}
        rows={rows}
        value={value}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder || 'Leave a note. Type @ to mention someone.'}
        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-200"
        data-testid={dataTestId || 'mention-textarea'}
      />
      {active && suggestions.length > 0 && (
        <div
          className="absolute z-30 mt-1 left-2 bg-white border border-slate-200 rounded shadow-lg max-h-56 overflow-y-auto w-72"
          data-testid="rd-mention-suggestions"
        >
          {suggestions.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-fuchsia-50 ${i === sel ? 'bg-fuchsia-100' : ''}`}
              data-testid={`rd-mention-opt-${i}`}
            >
              <div className="font-medium text-slate-800">@{(s.label || '').replace(/\s+/g, '')}</div>
              <div className="text-[11px] text-slate-500">{s.label} · {s.role || ''}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
